import React, { Component, ReactNode } from 'react';
import { List } from '../../domain/list/ListDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import ItemStatusComponent from '../ItemStatusComponent';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import FolderView from '../folder/view/FolderView';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import ListService from '../../service/ListService';
import LanguageUtil from '../../util/LanguageUtil';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment';
import Order = query.Order;

class ListViewerListComponent extends Component<Props, State> {
  public state: State = new State();

  private listService: ListService = ServiceContainer.getInstance().get(ServiceNames.LIST);

  public render() {
    const {
      lists,
      views,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && lists && lists.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={lists}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          componentName={ComponentName.LIST}
          openNewTab={true}
          onSelectionChange={this.onSelectionChange}
          rowClassName={this.onRowClassName}
          onRowClick={this.openEditor}
          columns={this.getColumns()}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.LIST} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.LIST} />
    );
  }

  public componentDidMount() {
    const { lists } = this.props;
    this.setState({ lists, loading: false });
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[16%]',
        copyable: true,
        onCopy: (e: List) => {
          return e.name;
        },
        body: (record: List) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          if (view == undefined) return null;
          return (
            <div className={'flex items-center justify-start text-xs 2xl:text-md'}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={25} text={record.name} data-testid={`${ComponentName.LIST}_name_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.status', defaultMessage: 'Status' }),
        className: 'w-1/7',
        body: (record: List) => {
          return (
            <ItemStatusComponent
              status={record.stage}
              containerClassName='justify-start items-center'
              data-testid={`${ComponentName.LIST}_stage_text_${record.name}`}
            />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[320px]',
        body: (record: List) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper clipSize={40} text={record.description || LanguageUtil.get({ id: 'common.label.noDescriptionList', defaultMessage: 'This list does not have any description' })} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.type', defaultMessage: 'Type' }),
        className: 'w-1/7',
        body: (record: List) => {
          return (
            <div className={'flex items-center justify-start text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.type == 'INTERNAL_LIST' ? LanguageUtil.get({ id: 'common.label.internalList', defaultMessage: 'Internal List' }) : LanguageUtil.get({ id: 'common.label.externalList', defaultMessage: 'External List' })} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.createdAt', defaultMessage: 'Created At' }),
        className: 'w-1/7',
        body: (record: List) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'w-1/7',
        body: (record: List) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];

    if (RoleHelper.canEdit('List')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: List) => {
    const { permissions } = this.props;

    const actions: ReactNode[] = [];

    const permission = permissions[record.name];

    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.LIST, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.LIST, [record.name], () => this.listService.undeployList(record)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.LIST}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.listService.removelist(record),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.LIST}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.listService.deployList(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.LIST}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openList } = this.props;
    openList(e.data.name);
  };

  private onRowClassName = (data: List) => {
    const { views } = this.props;
    const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
    return view ? `list_${view?.getModel().id}` : '';
  };
}

class State {
  lists: List[] = [];
  loading: boolean = true;
}

interface Props extends ReduxProps {
  lists: List[];
  openList: (id: string) => void;
  currentModal?: any;
  views: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ListViewerListComponent);
